
import { useState, useEffect, useCallback, useRef } from "react";
import { ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ScrollToTop() {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [tenthCardPosition, setTenthCardPosition] = useState<number | null>(null);
  const throttleRef = useRef<number>();

  // Throttle scroll events to reduce forced reflows
  const throttle = useCallback((func: () => void, limit: number) => {
    if (throttleRef.current) return;
    throttleRef.current = requestAnimationFrame(() => {
      func();
      throttleRef.current = undefined;
    });
  }, []);

  // Cache tenth card position to avoid repeated getBoundingClientRect calls
  const cacheTenthCardPosition = useCallback(() => {
    if (window.location.pathname === '/') {
      const businessCards = document.querySelectorAll('[data-business-card]');
      if (businessCards.length >= 10) {
        const tenthCard = businessCards[9];
        // Use Intersection Observer instead of getBoundingClientRect for better performance
        const observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (entry.isIntersecting) {
              // Estimate position based on viewport
              setTenthCardPosition(window.scrollY + window.innerHeight);
              observer.disconnect();
            }
          },
          { threshold: 0 }
        );
        observer.observe(tenthCard);
      }
    }
  }, []);

  useEffect(() => {
    // Cache card position on mount
    cacheTenthCardPosition();
  }, [cacheTenthCardPosition]);

  useEffect(() => {
    const toggleVisibility = () => {
      const isMainPage = window.location.pathname === '/';
      
      if (isMainPage) {
        // Use cached position to avoid getBoundingClientRect
        if (tenthCardPosition !== null) {
          setIsVisible(window.scrollY > tenthCardPosition);
        } else {
          // Fallback: show after scrolling past 2000px (roughly 10 cards)
          setIsVisible(window.scrollY > 2000);
        }
      } else {
        // On other pages, use simple threshold without layout reads
        setIsVisible(window.scrollY > window.innerHeight / 2);
      }
    };

    // Throttled scroll handler to reduce reflow frequency
    const throttledToggleVisibility = () => {
      throttle(toggleVisibility, 16); // ~60fps
    };

    // Check for mobile menu state by observing DOM changes
    const checkMobileMenu = () => {
      const mobileMenu = document.querySelector('[data-mobile-menu]');
      const isOpen = mobileMenu && mobileMenu.getAttribute('data-open') === 'true';
      setIsMobileMenuOpen(!!isOpen);
    };

    // Use passive listener for better performance
    window.addEventListener("scroll", throttledToggleVisibility, { passive: true });
    
    // Observer for mobile menu changes
    const observer = new MutationObserver(checkMobileMenu);
    observer.observe(document.body, { 
      childList: true, 
      subtree: true, 
      attributes: true,
      attributeFilter: ['data-open']
    });

    return () => {
      window.removeEventListener("scroll", throttledToggleVisibility);
      observer.disconnect();
      if (throttleRef.current) {
        cancelAnimationFrame(throttleRef.current);
      }
    };
  }, [throttle, tenthCardPosition]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Don't show if mobile menu is open
  const shouldShow = isVisible && !isMobileMenuOpen;

  return (
    <Button
      onClick={scrollToTop}
      className={`scroll-to-top transition-opacity duration-200 ${shouldShow ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      size="icon"
      aria-label="Scroll to top"
    >
      <ArrowUp className="h-5 w-5" />
    </Button>
  );
}
